import { KidDocumentsLegacy } from 'components/common-form/sidePanel/docToolbar/regulatory/documents-legacy/KidDocuments';
import { KidDocuments } from 'components/common-form/sidePanel/docToolbar/regulatory/documents/KidDocuments';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IRegulatoryRejection, IRegulatoryResource } from 'state/regulatoryDocuments';
import type { WorkspaceType } from 'state/workspaces/model';

import { DocumentRejectionErrorPanel, RegulatoryComponentWrapper } from './Components';
import { UnavailableKidRegulatoryComponent } from './Regulatory.nokid';

export interface KidRegulatoryComponentProps {
  tabId: string;
  workspaceType: WorkspaceType;

  isKidUnavailable: boolean;
  isKidRequired: boolean;
  isInternal: boolean;
  kidTimeoutMs: number;
  kidDocument: IRegulatoryResource[] | undefined;
  kidDocumentError: IRegulatoryRejection[] | undefined;
  kidDocumentTimeout: boolean;

  hasKidErrors: boolean;

  isKidDocumentLoading: boolean;
}

export const KidRegulatoryComponent: FC<KidRegulatoryComponentProps> = ({
  tabId,
  workspaceType,
  isKidRequired,
  isKidUnavailable,
  isInternal,
  kidDocument,
  kidDocumentError,
  kidDocumentTimeout,
  kidTimeoutMs,
  isKidDocumentLoading,
  hasKidErrors,
}) => {
  const { formatMessage } = useIntl();
  const isNextDesignEnabled = useToggleFeature('nextDocumentParis');

  const kidTimeoutMsg = useMemo(
    () =>
      formatMessage(
        { id: 'quote.errorMessages.regulationTimeout' },
        { name: 'KID', minutes: (kidTimeoutMs / 60_000).toFixed(1) },
      ),
    [formatMessage, kidTimeoutMs],
  );

  if (!isKidRequired) {
    return null;
  }

  if (isKidUnavailable) {
    return <UnavailableKidRegulatoryComponent />;
  }

  return (
    <RegulatoryComponentWrapper
      componentId="kid-accordion"
      title="blotterPanel.KidDocumentAccordionTitle"
      isLoading={isKidDocumentLoading}
      isInError={hasKidErrors}
      errorMessage={kidDocumentTimeout ? kidTimeoutMsg : undefined}
    >
      {kidDocument &&
        (isNextDesignEnabled ? (
          <div className="mt-3">
            <KidDocuments tabId={tabId} workspaceType={workspaceType} className="border border-secondary" />
          </div>
        ) : (
          <KidDocumentsLegacy tabId={tabId} workspaceType={workspaceType} />
        ))}
      {hasKidErrors && (
        <DocumentRejectionErrorPanel
          rejections={kidDocumentError!}
          containerId="kid-error"
          isInternal={isInternal}
          titleIntKey="errorDetails.kidDocument.standardMessage"
        />
      )}
    </RegulatoryComponentWrapper>
  );
};

KidRegulatoryComponent.displayName = 'KidRegulatoryComponent';
